import React, { useState, useEffect } from "react";
import {
  Bell,
  BellOff,
  Edit3,
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  Clock,
  Users,
  MessageSquare,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Info,
  CreditCard,
} from "lucide-react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";
import { modalSettingsService } from "../../lib/modalSettingsService";

const ModalSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    notice_modal_enabled: true,
    notice_modal_title: "",
    notice_modal_date: "",
    notice_modal_content: {
      intro: "",
      main: "",
      closing: "",
    },
    notice_modal_show_interval_hours: 6,
    notice_modal_target_audience: "affiliates",
    // Bank Accounts notice fields
    bank_notice_enabled: true,
    bank_notice_title: "",
    bank_notice_date: "",
    bank_notice_content: {
      intro: "",
      main: "",
      closing: "",
    },
    bank_notice_show_interval_hours: 6,
  });

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("🔧 Loading modal settings...");

      const data = await modalSettingsService.getSettings();
      setSettings(data);
      setFormData(data);

      console.log("✅ Modal settings loaded:", data);
    } catch (err) {
      console.error("❌ Error loading modal settings:", err);
      setError(`Failed to load settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      console.log("💾 Saving modal settings:", formData);

      const updatedSettings =
        await modalSettingsService.updateSettings(formData);
      setSettings(updatedSettings);
      setSuccess("Settings saved successfully!");
      setEditMode(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("❌ Error saving modal settings:", err);
      setError(`Failed to save settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async () => {
    try {
      setSaving(true);
      setError(null);

      const newEnabled = !formData.notice_modal_enabled;
      const updatedData = { ...formData, notice_modal_enabled: newEnabled };

      const updatedSettings =
        await modalSettingsService.updateSettings(updatedData);
      setSettings(updatedSettings);
      setFormData(updatedData);

      setSuccess(
        `Notice modal ${newEnabled ? "enabled" : "disabled"} successfully!`
      );
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("❌ Error toggling modal:", err);
      setError(`Failed to toggle modal: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        "Are you sure you want to reset all modal settings to defaults? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const defaultSettings = await modalSettingsService.resetToDefaults();
      setSettings(defaultSettings);
      setFormData(defaultSettings);
      setEditMode(false);

      setSuccess("Settings reset to defaults successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("❌ Error resetting settings:", err);
      setError(`Failed to reset settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleBankEnabled = async () => {
    try {
      setSaving(true);
      setError(null);

      const newEnabled = !formData.bank_notice_enabled;
      const updatedData = { ...formData, bank_notice_enabled: newEnabled };

      const updatedSettings =
        await modalSettingsService.updateSettings(updatedData);
      setSettings(updatedSettings);
      setFormData(updatedData);

      setSuccess(
        `Bank notice ${newEnabled ? "enabled" : "disabled"} successfully!`
      );
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("❌ Error toggling bank notice:", err);
      setError(`Failed to toggle bank notice: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 text-[#B8460E] mx-auto mb-4" />
          <p className="text-gray-400">Loading modal settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <MessageSquare className="h-6 w-6 text-[#B8460E]" />
            Modal Settings
          </h2>
          <p className="text-gray-400 mt-1 text-sm sm:text-base">
            Manage system-wide modal notifications and announcements
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 sm:self-end sm:ml-auto">
          <Button
            onClick={() => setPreviewMode(!previewMode)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={saving}
          >
            {previewMode ? (
              <EyeOff className="h-4 w-4 mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            {previewMode ? "Hide Preview" : "Preview"}
          </Button>

          {!editMode ? (
            <Button
              onClick={() => setEditMode(true)}
              className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
              disabled={saving}
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Edit Settings
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setEditMode(false);
                  setFormData(settings);
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      {/* Main Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Affiliate Notice Status */}
        <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            {formData.notice_modal_enabled ? (
              <Bell className="h-5 w-5 text-green-400" />
            ) : (
              <BellOff className="h-5 w-5 text-red-400" />
            )}
            Affiliate Notice Status
          </h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
              <div>
                <p className="font-medium text-white">
                  {formData.notice_modal_enabled ? "Enabled" : "Disabled"}
                </p>
                <p className="text-sm text-gray-400">
                  {formData.notice_modal_enabled
                    ? "Affiliates will see the affiliate notice"
                    : "Affiliate notice is hidden from affiliates"}
                </p>
              </div>
              <Button
                onClick={handleToggleEnabled}
                className={`${
                  formData.notice_modal_enabled
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                } text-white`}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="animate-spin h-4 w-4" />
                ) : formData.notice_modal_enabled ? (
                  "Disable"
                ) : (
                  "Enable"
                )}
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-blue-400" />
                  <span className="text-xs text-gray-400 uppercase tracking-wider">
                    Show Interval
                  </span>
                </div>
                <p className="text-lg font-bold text-white">
                  {formData.notice_modal_show_interval_hours}h
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-green-400" />
                  <span className="text-xs text-gray-400 uppercase tracking-wider">
                    Target
                  </span>
                </div>
                <p className="text-lg font-bold text-white capitalize">
                  {formData.notice_modal_target_audience}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Info className="h-5 w-5 text-[#B8460E]" />
            Quick Actions
          </h3>

          <div className="space-y-3">
            <Button
              onClick={handleReset}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white justify-start"
              disabled={saving}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>

            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-sm text-blue-300 mb-2">
                <strong>Debug Console Commands:</strong>
              </p>
              <div className="font-mono text-xs text-blue-400 space-y-1">
                <p>window.modalSettingsDebug.getSettings()</p>
                <p>window.modalSettingsDebug.debugTable()</p>
                <p>window.modalSettingsDebug.reset()</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Bank Notice Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            {formData.bank_notice_enabled ? (
              <CreditCard className="h-5 w-5 text-green-400" />
            ) : (
              <CreditCard className="h-5 w-5 text-red-400" />
            )}
            Bank Notice Status
          </h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
              <div>
                <p className="font-medium text-white">
                  {formData.bank_notice_enabled ? "Enabled" : "Disabled"}
                </p>
                <p className="text-sm text-gray-400">
                  {formData.bank_notice_enabled
                    ? "Bank Accounts page will show the bank notice banner"
                    : "Bank notice is hidden on the Bank Accounts page"}
                </p>
              </div>
              <Button
                onClick={handleToggleBankEnabled}
                className={`${formData.bank_notice_enabled ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"} text-white`}
                disabled={saving}
              >
                {formData.bank_notice_enabled ? "Disable" : "Enable"}
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-blue-400" />
                  <span className="text-xs text-gray-400 uppercase tracking-wider">
                    Show Interval
                  </span>
                </div>
                <p className="text-lg font-bold text-white">
                  {formData.bank_notice_show_interval_hours}h
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-green-400" />
                  <span className="text-xs text-gray-400 uppercase tracking-wider">
                    Target
                  </span>
                </div>
                <p className="text-lg font-bold text-white capitalize">
                  {formData.notice_modal_target_audience}
                </p>
              </div>
            </div>
          </div>
        </Card>
        <div className="hidden lg:block" />
      </div>

      {/* Content Editor - Affiliate Notice */}
      {editMode && (
        <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10">
          <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-[#B8460E]" />
            Edit Affiliate Notice Content
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Settings */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Modal Title
                </label>
                <input
                  type="text"
                  value={formData.notice_modal_title}
                  onChange={(e) =>
                    handleInputChange("notice_modal_title", e.target.value)
                  }
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                  placeholder="Enter modal title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Date Display
                </label>
                <input
                  type="text"
                  value={formData.notice_modal_date}
                  onChange={(e) =>
                    handleInputChange("notice_modal_date", e.target.value)
                  }
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                  placeholder="e.g., August 16, 2025"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Show Interval (hours)
                </label>
                <input
                  type="number"
                  min="1"
                  max="168"
                  value={formData.notice_modal_show_interval_hours}
                  onChange={(e) =>
                    handleInputChange(
                      "notice_modal_show_interval_hours",
                      parseInt(e.target.value)
                    )
                  }
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Target Audience
                </label>
                <select
                  value={formData.notice_modal_target_audience}
                  onChange={(e) =>
                    handleInputChange(
                      "notice_modal_target_audience",
                      e.target.value
                    )
                  }
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                >
                  <option value="affiliates">Affiliates Only</option>
                  <option value="all">All Users</option>
                  <option value="none">None (Disabled)</option>
                </select>
              </div>
            </div>

            {/* Content Sections */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Introduction Text
                </label>
                <textarea
                  value={formData.notice_modal_content.intro}
                  onChange={(e) =>
                    handleInputChange(
                      "notice_modal_content.intro",
                      e.target.value
                    )
                  }
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent resize-none"
                  placeholder="Enter introduction text..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Main Content
                </label>
                <textarea
                  value={formData.notice_modal_content.main}
                  onChange={(e) =>
                    handleInputChange(
                      "notice_modal_content.main",
                      e.target.value
                    )
                  }
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent resize-none"
                  placeholder="Enter main content..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Closing Text
                </label>
                <textarea
                  value={formData.notice_modal_content.closing}
                  onChange={(e) =>
                    handleInputChange(
                      "notice_modal_content.closing",
                      e.target.value
                    )
                  }
                  rows={2}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent resize-none"
                  placeholder="Enter closing text..."
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Content Editor - Bank Accounts Notice */}
      {editMode && (
        <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10">
          <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-[#B8460E]" />
            Edit Bank Accounts Notice Content
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                <div>
                  <p className="font-medium text-white">
                    {formData.bank_notice_enabled ? "Enabled" : "Disabled"}
                  </p>
                  <p className="text-sm text-gray-400">
                    Show bank notice banner on Bank Accounts page
                  </p>
                </div>
                <Button
                  onClick={() =>
                    handleInputChange(
                      "bank_notice_enabled",
                      !formData.bank_notice_enabled
                    )
                  }
                  className={`${formData.bank_notice_enabled ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"} text-white`}
                  disabled={saving}
                >
                  {formData.bank_notice_enabled ? "Disable" : "Enable"}
                </Button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bank Notice Title
                </label>
                <input
                  type="text"
                  value={formData.bank_notice_title}
                  onChange={(e) =>
                    handleInputChange("bank_notice_title", e.target.value)
                  }
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                  placeholder="Enter bank notice title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bank Notice Date Display
                </label>
                <input
                  type="text"
                  value={formData.bank_notice_date}
                  onChange={(e) =>
                    handleInputChange("bank_notice_date", e.target.value)
                  }
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                  placeholder="e.g., August 16, 2025"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bank Notice Show Interval (hours)
                </label>
                <input
                  type="number"
                  min="1"
                  max="168"
                  value={formData.bank_notice_show_interval_hours}
                  onChange={(e) =>
                    handleInputChange(
                      "bank_notice_show_interval_hours",
                      parseInt(e.target.value)
                    )
                  }
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                />
              </div>
            </div>

            {/* Content Sections */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Introduction Text
                </label>
                <textarea
                  value={formData.bank_notice_content.intro}
                  onChange={(e) =>
                    handleInputChange(
                      "bank_notice_content.intro",
                      e.target.value
                    )
                  }
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent resize-none"
                  placeholder="Enter introduction text..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Main Content
                </label>
                <textarea
                  value={formData.bank_notice_content.main}
                  onChange={(e) =>
                    handleInputChange(
                      "bank_notice_content.main",
                      e.target.value
                    )
                  }
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent resize-none"
                  placeholder="Enter main content..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Closing Text
                </label>
                <textarea
                  value={formData.bank_notice_content.closing}
                  onChange={(e) =>
                    handleInputChange(
                      "bank_notice_content.closing",
                      e.target.value
                    )
                  }
                  rows={2}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent resize-none"
                  placeholder="Enter closing text..."
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Preview - Affiliate Notice */}
      {previewMode && (
        <Card className="p-6 bg-black/40 backdrop-blur-xl border border-[#B8460E]/30">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#B8460E]" />
            Affiliate Notice Preview
          </h3>

          <div className="bg-black/60 backdrop-blur-sm rounded-2xl p-8 border border-white/10 max-w-lg mx-auto">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-3">
                <div className="p-3 bg-[#B8460E]/20 rounded-xl">
                  <Info className="h-6 w-6 text-[#B8460E]" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {formData.notice_modal_title}
                  </h2>
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Calendar className="h-4 w-4" />
                    <span>{formData.notice_modal_date}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-4">
                {formData.notice_modal_content.intro && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <p className="text-gray-300 leading-relaxed">
                      {formData.notice_modal_content.intro}
                    </p>
                  </div>
                )}

                {formData.notice_modal_content.main && (
                  <div className="bg-gradient-to-r from-[#B8460E]/10 to-blue-500/10 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <p className="text-gray-300 leading-relaxed">
                      {formData.notice_modal_content.main}
                    </p>
                  </div>
                )}

                {formData.notice_modal_content.closing && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <p className="text-gray-300 leading-relaxed">
                      {formData.notice_modal_content.closing}
                    </p>
                  </div>
                )}
              </div>

              {/* Action button */}
              <div className="flex justify-center">
                <Button className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white shadow-lg px-8 py-3">
                  Got it, thanks! 🚀
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Preview - Bank Notice */}
      {previewMode && (
        <Card className="p-6 bg-black/40 backdrop-blur-xl border border-[#B8460E]/30">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#B8460E]" />
            Bank Notice Preview
          </h3>

          <div className="bg-black/60 backdrop-blur-sm rounded-2xl p-8 border border-white/10 max-w-lg mx-auto">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-3">
                <div className="p-3 bg-[#B8460E]/20 rounded-xl">
                  <Info className="h-6 w-6 text-[#B8460E]" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {formData.bank_notice_title}
                  </h2>
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Calendar className="h-4 w-4" />
                    <span>{formData.bank_notice_date}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-4">
                {formData.bank_notice_content.intro && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <p className="text-gray-300 leading-relaxed">
                      {formData.bank_notice_content.intro}
                    </p>
                  </div>
                )}

                {formData.bank_notice_content.main && (
                  <div className="bg-gradient-to-r from-[#B8460E]/10 to-blue-500/10 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <p className="text-gray-300 leading-relaxed">
                      {formData.bank_notice_content.main}
                    </p>
                  </div>
                )}

                {formData.bank_notice_content.closing && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <p className="text-gray-300 leading-relaxed">
                      {formData.bank_notice_content.closing}
                    </p>
                  </div>
                )}
              </div>

              {/* Action button */}
              <div className="flex justify-center">
                <Button className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white shadow-lg px-8 py-3">
                  Got it, thanks! 🚀
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ModalSettings;
